/**
 * 74.16 (DECISIONS 385): the aurora as part of the planet system — one profile per world, `auroraOf(planet, system, body)`,
 * in place of the environmental effect a world rolled by archetype (mapgen still rolls it, so every later draw stays where
 * it was; nothing reads it now). Pure, no Three.js: rendering/aurora.ts draws it. Presentation only — nothing in the sim
 * reads it. It reads core/air.ts and core/spinAxis.ts and changes neither.
 *
 * - **Which worlds** (385 A): air × √(the star's wind at the world) × the world's field. No air, no glow (airOf's
 *   airless); thin air a faint one. The wind: the star's activity × the flux at the orbit (L / a², so 1 at Earth's
 *   light); a moon of a giant rides in the giant's field and its plasma (Io's torus: a floor on the wind). The field:
 *   seeded per world (its ground key). Under `AURORA.min` (a dial) a world shows none — about 40 of 60 battle worlds show one.
 * - **Where** (385 B): an oval of curtains about the magnetic pole (the spin axis turned a seeded few degrees), wider in a
 *   hard wind and a weak field; the curtains' feet over the air's rim (airOf's `rimOverR`, which already stands over the
 *   summits), so never through the ground or the people.
 * - **When** (385 C) is the drawing's: with the stars, dimmed by overcast, on the night side.
 * - **The look** (385 D): the air's own colours (the low edge, the high) by archetype.
 */
import type { PlanetState, PlanetArchetype, StarType, Vec3 } from '../types';
import type { StarSystem, SystemBody } from './systemGen';
import { airOf } from './air';
import { groundKey, spinAxisOf } from './spinAxis';
import { SeededRandom } from './random';
import { dsin, dcos } from './dmath';
import { vecAdd, vecCross, vecNormalize, vecScale } from '../math';

/** The air's aurora colours: the low edge (where a curtain is brightest) and the high (its fading top). */
export const AURORA_COLOURS: Record<PlanetArchetype | 'earth', { low: string; high: string; gas: string }> = {
  thalia:  { low: '#3cff86', high: '#ff4e72', gas: 'oxygen: green, red tops' },
  earth:   { low: '#3cff86', high: '#ff4e72', gas: 'oxygen: green, red tops' },
  pelago:  { low: '#3cffa0', high: '#c25cff', gas: 'oxygen and water: green, violet tops' },
  viridis: { low: '#6cff4c', high: '#ff5c8a', gas: 'thick oxygen: yellow-green, rose tops' },
  bruma:   { low: '#40ffc0', high: '#6c70ff', gas: 'cold oxygen and nitrogen: teal, blue tops' },
  murex:   { low: '#ff5cc8', high: '#8a5cff', gas: 'nitrogen: pink, violet tops' },
  sonora:  { low: '#8c7cff', high: '#ff7ab0', gas: 'thin nitrogen: violet, pink tops' },
  ochrus:  { low: '#ff6a48', high: '#ffa070', gas: 'thin carbon dioxide: a dull red' },
  tephra:  { low: '#ffb040', high: '#ff5a3c', gas: 'sulphur: amber, red tops' },
  pallor:  { low: '#b4ffd0', high: '#a0b0ff', gas: 'thin and cold: pale green' },
};

/** The star's activity: how hard its wind blows at Earth's light (a red dwarf flares; a giant's wind is slow). */
export const STAR_ACTIVITY: Record<StarType, number> = { redDwarf: 2.0, orangeDwarf: 1.2, yellowDwarf: 1.0, blueGiant: 1.6, redGiant: 0.5 };

const DEG = Math.PI / 180;
export const AURORA = {
  /** under this strength a world shows none (385: a dial) */
  min: 0.55,
  /** the air's share: full from this density */
  fullAir: 0.7,
  /** the wind's cap (a world close in to an active star) */
  maxWind: 3,
  /** the field, seeded: [min, max]; a moon of a giant × `giantMoon` */
  field: [0.4, 1.3] as const,
  giantMoon: 1.6,
  /** a giant's moon rides in the giant's own plasma: its wind at least this, however far the star */
  giantWind: 0.4,
  /** the magnetic pole off the spin axis (rad) */
  tilt: [4 * DEG, 12 * DEG] as const,
  /** the oval's colatitude (rad): the first in a calm wind and a strong field, to the second */
  colat: [12 * DEG, 28 * DEG] as const,
  /** the curtains' feet over the air's rim, and their height (fractions of R) */
  over: 0.01,
  tall: 0.05,
} as const;

export interface AuroraProfile {
  /** air × √wind × field: 0 none, about 1 a usual one, to about 2 */
  strength: number;
  /** strength ≥ AURORA.min */
  shown: boolean;
  /** the parts */
  air: number; wind: number; field: number; giantMoon: boolean;
  /** the magnetic pole (unit) and the oval's colatitude about it (rad) */
  pole: Vec3; colat: number;
  /** the curtains' feet and tops (radius ÷ R) */
  bottom: number; top: number;
  colours: { low: string; high: string; gas: string };
  /** a seed for the curtains' folds */
  seed: number;
}

/** The world's aurora. `system` and `body` are the sky's (rendering/systemSkyPlay.ts); without them, a yellow dwarf at Earth's light. */
export function auroraOf(planet: PlanetState, system: StarSystem | null = null, body: SystemBody | null = null): AuroraProfile {
  const airP = airOf(planet);
  const air = airP.airless ? 0 : Math.min(1, airP.density / AURORA.fullAir);
  const rng = new SeededRandom(`${groundKey(planet)}-aurora`);
  const parent = system && body && body.parent !== null ? system.bodies.find(b => b.id === body.parent) : undefined;
  const giantMoon = !!parent && (parent.kind === 'gasGiant' || parent.kind === 'iceGiant');
  const field = rng.range(AURORA.field[0], AURORA.field[1]) * (giantMoon ? AURORA.giantMoon : 1);
  const L = system ? system.star.luminosity : 1, a = body ? Math.max(1e-3, body.orbitAu) : 1;
  const wind = Math.max(giantMoon ? AURORA.giantWind : 0, Math.min(AURORA.maxWind, (system ? STAR_ACTIVITY[system.star.type] : 1) * L / (a * a)));
  const strength = air * Math.sqrt(wind) * field;
  // the magnetic pole: the spin axis turned a seeded few degrees toward a seeded azimuth
  const s = vecNormalize(spinAxisOf(planet));
  const tilt = rng.range(AURORA.tilt[0], AURORA.tilt[1]), az = rng.range(0, Math.PI * 2);
  const e1 = vecNormalize(vecCross(s, Math.abs(s.x) < 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 0, z: 1 })), e2 = vecCross(s, e1);
  const side = vecAdd(vecScale(e1, dcos(az)), vecScale(e2, dsin(az)));
  const pole = vecNormalize(vecAdd(vecScale(s, dcos(tilt)), vecScale(side, dsin(tilt))));
  // the oval: wider in a hard wind and a weak field
  const t = Math.max(0, Math.min(1, (wind / field - 0.5) / 2.5));
  const colat = AURORA.colat[0] + (AURORA.colat[1] - AURORA.colat[0]) * t;
  const bottom = airP.rimOverR + AURORA.over;
  const colours = (planet.palette?.name === 'earth' ? AURORA_COLOURS.earth : undefined) ?? AURORA_COLOURS[planet.archetype as PlanetArchetype] ?? AURORA_COLOURS.thalia;
  return { strength, shown: strength >= AURORA.min, air, wind, field, giantMoon, pole, colat, bottom, top: bottom + AURORA.tall, colours, seed: rng.range(0, 100) };
}
